import * as React from 'react';
import {Button, Modal, Table} from 'antd';
import locales from '../locales';
import * as tableUtils from '../utils/table-utils';
import {GridState, searchableSortableColumn, simpleColumn} from '../utils/table-utils';
import {ErrorAlert} from '../common/ErrorAlert';
import {Contact, ErrorResponse, PageResponseEnvelope} from '../api/dtos/dtos';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import furoApi from '../api/FuroApi';
import {ColumnProps} from 'antd/lib/table';
import ClientContactForm from './ClientContactForm';
import ButtonBar from '../common/ButtonBar';
import {showDeleteConfirm, showDeleteSuccessNotification, showSaveSuccessNotification} from "../utils/dialogs";
import ButtonGroup from "antd/lib/button/button-group";

interface ClientRepsEditorProps {
  clientId: number;
  editable: boolean;
}

interface ClientRepsEditorState extends GridState<Contact> {
  editedContact?: Contact;
  error?: ErrorResponse;
}

class ClientContactsEditor extends React.Component<ClientRepsEditorProps, ClientRepsEditorState> {

  public readonly loadingHandler?: LoadingOverlayHandler;

  public state: ClientRepsEditorState = {
    ...tableUtils.initialGridState(),
    sortColumnKey: 'name',
    sortOrder: 'ascend',
  };

  public componentDidMount() {
    this.reload();
  }

  public render(): React.ReactNode {
    const {editable} = this.props;
    const {data, editedContact, error} = this.state;

    const repsColumns: Array<ColumnProps<Contact>> = [
      searchableSortableColumn(locales.name, 'name', this.state, r => r.name),
      searchableSortableColumn(locales.title, 'title', this.state, r => r.title),
      simpleColumn(locales.tel, r => r.tel),
      simpleColumn(locales.email, r => r.email),
      {key: 'actions', width: 80, render: this.renderActionColumn}
    ];

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <Modal
          title={locales.edit_contact}
          visible={editedContact !== undefined}
          footer={null}
          onCancel={this.onFormCancel}
          destroyOnClose={true}
        >
          {editedContact && <ClientContactForm
              contact={editedContact}
              onFormSubmit={this.onFormSubmit}
              onFormCancel={this.onFormCancel}
          />}
        </Modal>

        {editable && <ButtonBar style={{marginBottom: 8}}>
            <Button size="small" type="primary" icon="plus"
                    onClick={this.onAddContactClick}>{locales.add}</Button>
        </ButtonBar>}

        <Table
          dataSource={data}
          columns={repsColumns}
          rowKey={tableUtils.idRowKeyFn}
          bordered={true}
          size="small"
          pagination={{
            ...tableUtils.defaultPaginationConfig(this.state),
            defaultPageSize: 6,
            showSizeChanger: false,
          }}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
          scroll={{x: 512}}
        />

        {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}
      </>
    );
  }

  private renderActionColumn = (text: any, record: Contact) => {
    const onEditClick = () => this.onEditClick(record);
    const onDeleteClick = () => this.onDeleteClick(record);
    return this.props.editable && (
      <div className="action-column" style={{textAlign: 'right'}}>
        <ButtonGroup>
          <Button icon="edit" onClick={onEditClick} title={locales.edit}/>
          <Button icon="delete" onClick={onDeleteClick} title={locales.delete}/>
        </ButtonGroup>
      </div>
    );
  };

  private onGridChange = (newState: GridState<Contact>) => {
    this.setState({...newState}, () => this.reload());
  };

  private reload = () => {
    const {clientId} = this.props;
    const request = tableUtils.toGridRequest(this.state);
    const promise = furoApi.clients.filterContacts(clientId, request)
      .then(this.onReloadSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onReloadSuccess = (response: PageResponseEnvelope<Contact>) => {
    this.setState({
      ...tableUtils.toGridState(response),
      error: undefined,
    });
  };

  private onEditClick = (contact: Contact) => {
    this.setState({editedContact: contact,});
  };

  private onAddContactClick = () => {
    this.setState({
      editedContact: {
        id: null,
        name: '',
        title: null,
        tel: null,
        email: null,
        idNumber: null,
        mothersName: null,
        deleted: false,
      },
    });
  };

  private onFormCancel = () => {
    this.setState({editedContact: undefined,});
  };

  private onFormSubmit = (contact: Contact) => {
    const {clientId} = this.props;
    if (contact.id === null) {
      // create mode
      withLoadingOverlay(
        furoApi.clients.createContact(clientId, contact)
          .then(this.onSaveReprSuccess)
          .catch(this.onApiError), this.loadingHandler);

    } else {
      // edit mode
      withLoadingOverlay(
        furoApi.clients.saveContact(clientId, contact, contact.id)
          .then(this.onSaveReprSuccess)
          .catch(this.onApiError), this.loadingHandler);
    }
  };

  private onSaveReprSuccess = () => {
    this.setState({editedContact: undefined,});
    showSaveSuccessNotification();
    this.reload();
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  };

  private onDeleteClick = (record: Contact) => {
    showDeleteConfirm(() => this.deleteContact(record));
  };

  private deleteContact = (contact: Contact) => {
    const {clientId} = this.props;
    const promise = furoApi.clients.deleteContact(clientId, contact.id!!)
      .then(() => {
        showDeleteSuccessNotification();
        this.reload();
      })
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };
}

export default ClientContactsEditor;


// WEBPACK FOOTER //
// ./src/client-details/ClientContactsEditor.tsx